import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import type { CentralPendenciasResumo, ItemCentral, TipoItemCentral } from '@/types/pendencia'

/** Rótulos dos tipos, na ordem em que o backend os entrega (o mais urgente primeiro). */
export const TIPO_ITEM_CENTRAL_LABEL: Record<TipoItemCentral, string> = {
  PENDENCIA: 'Pendência',
  DOCUMENTO_FALTANDO: 'Documento faltando',
  DOCUMENTO_RECUSADO: 'Documento recusado',
  NF_A_EMITIR: 'NF a emitir',
  PROXIMA_ACAO: 'Próxima ação',
  REUNIAO: 'Reunião',
}

const TIPO_COR: Record<TipoItemCentral, 'warning' | 'error' | 'info' | 'default' | 'success'> = {
  PENDENCIA: 'warning',
  DOCUMENTO_FALTANDO: 'error',
  DOCUMENTO_RECUSADO: 'error',
  NF_A_EMITIR: 'success',
  PROXIMA_ACAO: 'default',
  REUNIAO: 'info',
}

/** Filtros e a chave do resumo que conta cada um (a contagem vem do backend, não da tela). */
const FILTROS: { valor: TipoItemCentral; rotulo: string; contagem: keyof CentralPendenciasResumo }[] = [
  { valor: 'PENDENCIA', rotulo: 'Pendências', contagem: 'pendencias' },
  { valor: 'DOCUMENTO_FALTANDO', rotulo: 'Documentos', contagem: 'documentosFaltando' },
  // P3: "Documento recusado: X" — descrição = motivo da recusa (sem o autor).
  { valor: 'DOCUMENTO_RECUSADO', rotulo: 'Recusados', contagem: 'documentosRecusados' },
  { valor: 'NF_A_EMITIR', rotulo: 'NF a emitir', contagem: 'nfAEmitir' },
  { valor: 'PROXIMA_ACAO', rotulo: 'Próximas ações', contagem: 'proximasAcoes' },
  { valor: 'REUNIAO', rotulo: 'Reuniões', contagem: 'reunioes' },
]

type Filtro = TipoItemCentral | 'TODOS'

const fmtData = (iso?: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR') : null)
const fmtDataHora = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

const quando = (i: ItemCentral): string => {
  if (i.prazo) return `Prazo ${fmtData(i.prazo)}`
  if (i.dataHora) return fmtDataHora(i.dataHora) ?? '—'
  return '—'
}

const reserva = (i: ItemCentral): string => {
  const numero = i.reservaNumero != null ? `#${i.reservaNumero}` : null
  return [numero, i.empresa].filter(Boolean).join(' · ') || '—'
}

interface Props {
  /** Itens de `GET /v1/pendencias/central` (a página carrega uma vez e repassa). */
  itens: ItemCentral[]
  /** Resumo da MESMA resposta — dá a contagem de cada filtro. */
  resumo?: CentralPendenciasResumo | null
  carregando: boolean
  /** Mensagem do erro de carga (nulo = sem erro). */
  erro?: string | null
  semPermissao?: boolean
  onTentarNovamente: () => void
}

/**
 * Central de Pendências do consultor (VC-09 parte 2, Onda 2 / DEV-4): a lista de
 * `GET /v1/pendencias/central` — pendências dele, documentos obrigatórios faltando, NF a
 * emitir, próximas ações e reuniões —, com filtro por tipo e "Resolver →" para o `destino`
 * que o backend devolve. Nada é calculado aqui: as contagens dos filtros vêm do resumo.
 * Componente de apresentação — quem carrega é a página (uma chamada para lista + KPIs).
 */
const CentralPendenciasLista: FC<Props> = ({
  itens,
  resumo,
  carregando,
  erro,
  semPermissao = false,
  onTentarNovamente,
}) => {
  const navigate = useNavigate()
  const [filtro, setFiltro] = useState<Filtro>('TODOS')

  const visiveis = useMemo(
    () => (filtro === 'TODOS' ? itens : itens.filter((i) => i.tipo === filtro)),
    [itens, filtro]
  )

  const colunas: ColunaTabelaAcao<ItemCentral>[] = [
    {
      titulo: 'Tipo',
      render: (i) => <Chip size="small" color={TIPO_COR[i.tipo]} label={TIPO_ITEM_CENTRAL_LABEL[i.tipo]} />,
    },
    {
      titulo: 'O que fazer',
      render: (i) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {i.titulo}
          </Typography>
          {i.descricao && (
            <Typography variant="caption" color="text.secondary">
              {i.descricao}
            </Typography>
          )}
        </Box>
      ),
    },
    { titulo: 'Reserva', render: reserva },
    { titulo: 'Prazo / quando', render: quando },
    {
      titulo: 'Bloqueante',
      render: (i) => (i.bloqueante ? <Chip size="small" color="error" variant="outlined" label="Bloqueante" /> : '—'),
    },
  ]

  if (carregando) return <EstadoDeLista estado="carregando" minHeight={160} />
  if (semPermissao) return <EstadoDeLista estado="semPermissao" minHeight={160} />
  if (erro)
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar a Central de Pendências"
        detalhe={erro}
        acao={{ label: 'Tentar novamente', onClick: onTentarNovamente }}
        minHeight={160}
      />
    )

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2, overflowX: 'auto' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v: Filtro | null) => v && setFiltro(v)}
          aria-label="Filtrar por tipo"
        >
          <ToggleButton value="TODOS">Todos{resumo ? ` (${resumo.total})` : ''}</ToggleButton>
          {FILTROS.map((f) => (
            <ToggleButton key={f.valor} value={f.valor}>
              {f.rotulo}
              {resumo ? ` (${resumo[f.contagem] ?? 0})` : ''}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {visiveis.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem={filtro === 'TODOS' ? 'Nada pendente por aqui' : 'Nenhum item deste tipo'}
          detalhe={filtro === 'TODOS' ? 'Quando a Originei pedir algo ou um prazo chegar, aparece aqui.' : undefined}
          minHeight={160}
        />
      ) : (
        <TabelaAcao
          aria-label="Central de Pendências"
          colunas={colunas}
          linhas={visiveis}
          chaveDaLinha={(i) => i.id}
          onAbrir={(i) => navigate(i.destino)}
          renderAcao={() => 'Resolver →'}
        />
      )}
    </Box>
  )
}

export default CentralPendenciasLista
